import { AccessorFn, ColumnDef, DeepKeys } from "@tanstack/react-table";
import { TableCell } from "@/components/ui/table";
import { Check, X } from "lucide-react";
import { CellHoverClassName } from "@/components/List/ListTable/TableBody/Rows/TableDataRow";

type BooleanColumnOptions<T> = Omit<ColumnDef<T>, "accessorKey"> & {
	accessorKey?: DeepKeys<T>;
	accessorFn?: AccessorFn<T>;
	header?: string;
	align?: "left" | "center" | "right";
	className?: string;
	iconSize?: number;
	meta?: Record<string, unknown>;
};

export const BooleanColumn = <T,>({
	accessorKey,
	accessorFn,
	header,
	align = "center",
	className = "",
	iconSize = 16,
	meta,
	...rest
}: BooleanColumnOptions<T>): ColumnDef<T> => {
	const exportFormat = (value: unknown) => (value ? "Yes" : "No");

	const alignment = {
		left: "justify-start",
		center: "justify-center",
		right: "justify-end",
	}[align];

	return {
		accessorKey: accessorKey as string,
		header,
		meta: { wrapWithTableCell: false, exportFormat, cellClassName: className, ...meta },
		cell: ({ getValue }) => {
			const value = Boolean(getValue());
			const Icon = value ? Check : X;
			const iconClass = value ? "text-green-600" : "text-red-500";

			return (
				<TableCell className={`${CellHoverClassName} ${className}`}>
					<div className={`flex items-center ${alignment} w-full h-full`}>
						<Icon size={iconSize} className={iconClass} strokeWidth={2} />
					</div>
				</TableCell>
			);
		},
		...rest,
	};
};
